import type { EnterpriseProgram_SkillsetAvailabilityType as SkillsetAvailabilityType } from '__generated__/graphql-types';
import { CareerAcademy_Variant as CareerAcademyVariant } from '__generated__/graphql-types';

import { isAuthenticatedUser } from 'js/lib/user';

import { useEnterpriseMyPageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import useIsLimitedContent from 'bundles/composable-page-header-enterprise/hooks/useIsLimitedContent';
import type { Links } from 'bundles/megamenu/constants/simplifiedMegaMenuContent';
import { useDomainPills } from 'bundles/page-config-common/contexts/DomainsPillsContext';
import { useEnterpriseSkills } from 'bundles/page-config-common/contexts/enterprise/EnterpriseSkillsContext';
import type { TrackedSkillFragment } from 'bundles/page-config-common/queries/__generated__/QueryAdminSelectedSkills';
import type { PageConfig_DomainPillsComponentMetadataFragment as DomainPillsComponentType } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfig_DomainPillsComponentMetadata';
import { useCareerAcademyVariant } from 'bundles/unified-common/contexts/CareerAcademyVariantContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

import _t from 'i18n!nls/composable-page-header-enterprise';

const generateExploreMoreLinks = (
  baseProgramUrl: string,
  hasTargetSkills: boolean,
  skillsetAvailabilityType?: SkillsetAvailabilityType | null
) => {
  const links = [
    {
      label: _t('Tailored Learning'),
      id: 'tailored-learning',
      url: `${baseProgramUrl}/view-all-collections/tailored-learning`,
    },
    {
      label: _t('Selected Collections'),
      id: 'selected-collections',
      url: `${baseProgramUrl}/view-all-collections/curated`,
    },
  ];

  if (
    skillsetAvailabilityType &&
    skillsetAvailabilityType !== 'SKILL_SET_AVAILABILITY_TYPE_INVALID' &&
    hasTargetSkills
  ) {
    links.push({
      label: _t('SkillSets'),
      id: 'skillsets',
      url: `${baseProgramUrl}/skillsets`,
    });
  }

  return links;
};

const generateSkillsLinks = (
  baseProgramUrl: string,
  latestSkillsWithProgress?: TrackedSkillFragment[],
  hasProgramSpecificSkills?: boolean
) => {
  if (!latestSkillsWithProgress?.length) {
    return [];
  }
  const skills =
    latestSkillsWithProgress.map((skill) => ({
      label: skill.skillName,
      id: skill.id,
      url: `${baseProgramUrl}/skills/${skill.id}?source=${
        hasProgramSpecificSkills ? 'navigation-admin-highlight' : 'navigation-trending'
      }`,
    })) || [];

  return skills;
};

const generateCategoriesLinks = (
  domains: DomainPillsComponentType['domains'] | null | undefined,
  baseProgramUrl: string
) => {
  if (!domains) {
    return [];
  }
  return domains.map((pill) => ({
    label: pill.name,
    id: pill.slug,
    url: `${baseProgramUrl}/${pill.slug}`,
  }));
};

const generateExploreMoreCourseraLinks = (
  baseProgramUrl: string,
  isAuthenticated: boolean,
  showRecsFromProgram: boolean,
  showRecsFromCoursera: boolean,
  isProgramMember: boolean,
  isLimitedContent: boolean,
  showCareerAcademy: boolean
) => {
  const links = [];

  if (isAuthenticated && isProgramMember && !isLimitedContent && showRecsFromProgram) {
    links.push({
      label: _t('Personalized Learning'),
      id: 'personalized-learning',
      url: `${baseProgramUrl}/recommendations`,
    });
  }

  if (showRecsFromCoursera && !isLimitedContent) {
    links.push({
      label: _t('Popular on Coursera'),
      id: 'popular-learning',
      url: `${baseProgramUrl}/view-all-collections/browse`,
    });
  }

  if (!isLimitedContent) {
    links.push({
      label: _t('Professional Certificates'),
      id: 'professional-certificates',
      url: `${baseProgramUrl}/browse?productTypeDescription=Professional%20Certificates&sortBy=BEST_MATCH`,
    });
  }

  if (showCareerAcademy) {
    links.push({
      label: _t('Find your New Career'),
      id: 'career-academy',
      url: `/career-academy${baseProgramUrl}`,
    });
  }

  return links;
};

const useEnterpriseMegaMenuLinks = () => {
  const enterpriseProgram = useProgram();
  const productFeatures = useEnterpriseProductFeatures();
  const { adminSelectedSkills, hasProgramSpecificSkills } = useEnterpriseSkills();
  const { domains } = useDomainPills();
  const { currentPageProgram, enrolledEnterprisePrograms } = useEnterpriseMyPageHeader();
  const isLimitedContent = useIsLimitedContent();
  const isAuthenticated = isAuthenticatedUser();

  const careerAcademyVariant = useCareerAcademyVariant();
  const isCareerAcademy = !!careerAcademyVariant && careerAcademyVariant !== CareerAcademyVariant.None;

  const isProgramMember = enrolledEnterprisePrograms?.some(
    (program) => program.naptimeThirdPartyOrganization.id === currentPageProgram?.naptimeThirdPartyOrganization.id
  );

  if (!currentPageProgram) return undefined;
  const baseProgramUrl = `/programs/${currentPageProgram.slug}`;
  const skillsLinks = generateSkillsLinks(baseProgramUrl, adminSelectedSkills, hasProgramSpecificSkills);

  const enterpriseMegaMenu: Links = [
    {
      title: _t(`From #{programName}`, { programName: currentPageProgram.name }),
      id: 'from-program',
      links: generateExploreMoreLinks(
        baseProgramUrl,
        !!enterpriseProgram?.naptimeLinkedTargetSkillProfiles?.length,
        currentPageProgram.skillsetAvailabilityType
      ),
    },
    hasProgramSpecificSkills && {
      title: _t(`Skills selected by #{programName}`, { programName: currentPageProgram.name }),
      id: 'skills',
      links: skillsLinks,
    },
    domains?.length &&
      !isLimitedContent && {
        title: _t('Categories'),
        id: 'categories',
        links: generateCategoriesLinks(domains, baseProgramUrl),
      },
    !hasProgramSpecificSkills &&
      skillsLinks.length && {
        title: _t('Trending skills'),
        id: 'skills',
        url:
          isProgramMember && productFeatures?.enableSkillsDashboard
            ? `${baseProgramUrl}/my-learning?myLearningTab=SKILLS_DASHBOARD`
            : undefined,
        links: skillsLinks,
      },
    (!isLimitedContent || isCareerAcademy) && {
      title: _t('Explore more'),
      id: 'explore-more-from-coursera',
      links: generateExploreMoreCourseraLinks(
        baseProgramUrl,
        isAuthenticated,
        !!productFeatures?.enablePersonalizedRecommendationsForProgram,
        !productFeatures?.programHideCourseraRecommendations,
        !!isProgramMember,
        isLimitedContent,
        isCareerAcademy
      ),
    },
  ].filter(Boolean);

  return enterpriseMegaMenu;
};

export default useEnterpriseMegaMenuLinks;
